import {
  ACCESS_TOKEN_STORAGE_KEY,
  AUTH_REFRESH_ENDPOINT,
  SAML_ORGANIZATION_UUID_STORAGE_KEY,
  SSO_WARNING_PARAM,
} from "@frontend-shared/constants";
import {
  EAnalyticsAuthMethod,
  EAnalyticsEvent,
} from "@frontend-shared/product-analytics";
import type { IRefreshTokenResponse } from "@frontend-shared/types";
import { resolveSsoWarningKey } from "@frontend-shared/utils";
import axios from "axios";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { PATHS } from "@/constants/paths";
import { TOAST_TITLE_UNCLAMPED_CLASS_NAME } from "@/constants/toast";
import { useAuth } from "@/hooks/use-auth";
import { useProductAnalytics } from "@/hooks/use-product-analytics";
import { removeTabProjectUuid } from "@/lib/auth";

import { SSO_WARNING_TOAST_DURATION_MS } from "./constants/sso-warning";
import { clearSsoAutoRedirectAttempt } from "./utils";

// NOTE: SSO / magic-link landing page. The server sets the httpOnly
// refreshToken cookie before redirecting here, and the access token is
// obtained by exchanging that cookie via POST /auth/refresh — it must
// never arrive in the URL, because query strings leak into Referer
// headers, browser history, and proxy logs. Raw axios (not the shared
// ApiClient) keeps this exchange outside the client's 401-refresh
// interceptor loop, mirroring the websocket refresh helpers.
const Token = () => {
  const { getUser } = useAuth();
  const { capture } = useProductAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const [searchParams] = useSearchParams();
  const samlOrganizationUuid = searchParams.get("samlOrganizationUuid");
  // NOTE: NSAI-6755 — only a listed warning code resolves; a forged param
  // resolves to null and shows nothing.
  const ssoWarningKey = resolveSsoWarningKey({
    code: searchParams.get(SSO_WARNING_PARAM),
  });
  // NOTE: Refresh rotates the cookie server-side, so React StrictMode's
  // double-mounted effect must not fire the exchange twice — the second
  // call would race the first with an already-consumed token.
  const isExchangeStartedRef = useRef(false);

  useEffect(() => {
    if (isExchangeStartedRef.current) {
      return;
    }
    isExchangeStartedRef.current = true;

    const exchangeRefreshCookieForAccessToken = async (): Promise<void> => {
      try {
        const response = await axios.post<IRefreshTokenResponse>(
          AUTH_REFRESH_ENDPOINT,
        );
        const accessToken = response.data?.accessToken;

        if (!accessToken) {
          navigate(PATHS.AUTH.SIGN_IN, { replace: true });
          return;
        }

        // NOTE: NSAI-7394 — a new identity starts without this tab's old
        // project pointer, which a logout in another tab may not have reached.
        removeTabProjectUuid();
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);

        // NOTE: NSAI-6319 — a login that reached this page completed, so the
        // SSO page's loop cooldown has nothing left to protect against and is
        // released here rather than left to expire. It is what keeps signing
        // out and back in immediately from landing on the email form on a
        // single-organization deployment, where the auto-redirect IS the login
        // flow.
        clearSsoAutoRedirectAttempt();

        if (samlOrganizationUuid) {
          localStorage.setItem(
            SAML_ORGANIZATION_UUID_STORAGE_KEY,
            samlOrganizationUuid,
          );
        }

        // NOTE: NSAI-4615 — every redirect-based sign-in completes HERE, not
        // in `useSignin`, which only handles email+password. Without this the
        // activation funnel silently omitted every enterprise login: those
        // users appeared to arrive already signed in, with no event to start
        // the funnel from.
        //
        // NOTE: Only two methods can reach this page, so the branch below is
        // exhaustive: `getMagicLinkRedirectUri` (server) is called from a
        // single place, `respondWithTokens`, which handles the social OAuth
        // callbacks (google / github / yandex / vk / telegram) and SAML. The
        // server calls that redirect "magic-link", but it is the name of the
        // cookie-then-redirect mechanism, not an email sign-in link — there
        // is no passwordless email login landing here, which is why
        // `EAnalyticsAuthMethod` has no member for one.
        capture({
          event: EAnalyticsEvent.AUTH_LOGIN_COMPLETED,
          properties: {
            method: samlOrganizationUuid
              ? EAnalyticsAuthMethod.SSO
              : EAnalyticsAuthMethod.OAUTH,
          },
        });

        // NOTE: NSAI-6755 — raised only once the sign-in has actually
        // succeeded, and on every such sign-in: the organization's SSO domain
        // is unverified and works only through a temporary compatibility mode.
        // The toaster is mounted above the routes, so the toast outlives the
        // navigation below.
        if (ssoWarningKey) {
          toast.warning(t(ssoWarningKey), {
            duration: SSO_WARNING_TOAST_DURATION_MS,
            // NOTE: NSAI-6755 — three lines cut this warning off right before
            // its instruction (who verifies the domain, and where).
            classNames: { title: TOAST_TITLE_UNCLAMPED_CLASS_NAME },
          });
        }

        getUser();
        navigate(PATHS.CHAT, { replace: true });
      } catch {
        navigate(PATHS.AUTH.SIGN_IN, { replace: true });
      }
    };

    void exchangeRefreshCookieForAccessToken();
  }, [samlOrganizationUuid, ssoWarningKey, navigate, getUser, capture, t]);

  return null;
};
export default Token;
